// src/components/dashboards/LibraryDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import StatCard from '../shared/StatCard';
import { libraryAPI } from '../../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import JsBarcode from 'jsbarcode';
import Calendar from '../shared/Calendar';
import { notify, confirmDialog } from '../../utils/notify';
import '../../styles/Dashboard.css';

const LibraryDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === 'ADMIN' || user?.role === 'LIBRARY_STAFF';

  const getFileUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const backendBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
    return `${backendBase}${url}`;
  };

  const handleGoBack = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    switch (user.role) {
      case 'ADMIN':
        navigate('/admin-dashboard');
        break;
      case 'STUDENT':
        navigate('/student-dashboard');
        break;
      case 'FACULTY':
        if (user.subrole === 'HEAD_OF_DEPARTMENT') {
          navigate('/hod-dashboard');
        } else {
          navigate('/faculty-dashboard');
        }
        break;
      case 'PRINCIPAL':
        navigate('/principal-dashboard');
        break;
      case 'DEAN':
        navigate('/dean-dashboard');
        break;
      case 'FINANCE':
        navigate('/finance-dashboard');
        break;
      case 'HOSTEL_ADMIN':
        navigate('/hostel-dashboard');
        break;
      case 'LIBRARY_STAFF':
        navigate('/library-dashboard');
        break;
      default:
        window.history.back();
    }
  };

  // State Management
  const [activeTab, setActiveTab] = useState(isStaff ? 'overview' : 'borrower-overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Domain Data States
  const [stats, setStats] = useState({
    total_books: 0,
    total_copies: 0,
    issued_books: 0,
    available_books: 0,
    overdue_books: 0,
    active_members: 0,
    outstanding_fines: 0,
    collected_fines: 0,
    total_reservations: 0,
    digital_resources: 0,
    room_occupancy: 0
  });

  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [issues, setIssues] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [fines, setFines] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [digitalResources, setDigitalResources] = useState([]);
  const [journals, setJournals] = useState([]);
  const [readingRooms, setReadingRooms] = useState([]);
  const [readingRoomEntries, setReadingRoomEntries] = useState([]);
  const [settings, setSettings] = useState({});
  const [myMembership, setMyMembership] = useState(null);
  const [myIssues, setMyIssues] = useState([]);
  const [myFines, setMyFines] = useState([]);
  const [reportsData, setReportsData] = useState({
    monthly_issues: 0,
    monthly_returns: 0,
    fines_collected: 0,
    fines_waived: 0,
    top_books: [],
    recent_transactions: []
  });

  // Modals & Action States
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', code: '', description: '', color: '#3b82f6' });

  const [showBookModal, setShowBookModal] = useState(false);
  const [currentBook, setCurrentBook] = useState(null);
  const [generatedBarcodes, setGeneratedBarcodes] = useState([]);
  const [bookForm, setBookForm] = useState({
    title: '', isbn: '', authors: '', publisher: '', edition: '',
    publication_year: new Date().getFullYear(), description: '', language: 'EN',
    shelf_number: '', rack_number: '', price: 0, total_copies: 1, categories: [],
    purchase_date: '', vendor: ''
  });

  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({
    member_id: '', copy_barcode: '', issue_days: 14
  });
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [matchedMembers, setMatchedMembers] = useState([]);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null); // Book context for issue modal

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnForm, setReturnForm] = useState({
    issue_id: '', condition: 'GOOD', damage_notes: '', fine_amount: 0, waive_fine: false
  });

  const [showFineModal, setShowFineModal] = useState(false);
  const [fineForm, setFineForm] = useState({
    fine_id: '', payment_method: 'CASH', amount_paid: 0, transaction_reference: '', notes: ''
  });

  const [showDigitalModal, setShowDigitalModal] = useState(false);
  const [digitalForm, setDigitalForm] = useState({
    title: '', authors: '', resource_type: 'EBOOK', access_level: 'ALL',
    allowed_courses: [], external_url: '', file: null, description: '', tags: ''
  });

  const [showJournalModal, setShowJournalModal] = useState(false);
  const [currentJournal, setCurrentJournal] = useState(null);
  const [journalForm, setJournalForm] = useState({
    name: '', issn: '', publisher: '', frequency: 'Monthly',
    journal_type: 'PRINT', subscription_start: '', subscription_end: '',
    subscription_cost: 0, status: 'ACTIVE', digital_url: ''
  });

  const [showRoomEntryModal, setShowRoomEntryModal] = useState(false);
  const [roomEntryForm, setRoomEntryForm] = useState({
    room_id: '', member_id: '', seat_number: ''
  });

  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    fine_rate_student: 5, fine_rate_faculty: 10,
    duration_student: 14, duration_faculty: 30,
    max_books_student: 3, max_books_faculty: 5
  });

  // Search/Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [reportType, setReportType] = useState('summary');

  // Trigger alert messages
  const showAlert = (msg, isSuccess = true) => {
    if (isSuccess) {
      setSuccess(msg);
      setError('');
    } else {
      setError(msg);
      setSuccess('');
    }
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 5000);
  };

  // Helper to extract list from paginated or non-paginated API responses
  const getResultsArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.results && Array.isArray(data.results)) return data.results;
    return [];
  };

  // Initial Data Fetching
  useEffect(() => {
    fetchInitialData();
  }, [isStaff, activeTab]);

  // Live search members
  useEffect(() => {
    if (memberSearchQuery.trim().length >= 1) {
      if (selectedMember && memberSearchQuery === `${selectedMember.user_name} (${selectedMember.membership_id})`) {
        return;
      }
      const delayDebounceFn = setTimeout(async () => {
        try {
          const res = await libraryAPI.getMembers({ search: memberSearchQuery });
          setMatchedMembers(getResultsArray(res));
          setShowMemberDropdown(true);
        } catch (err) {
          console.error(err);
        }
      }, 250);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setMatchedMembers([]);
      setShowMemberDropdown(false);
    }
  }, [memberSearchQuery, selectedMember]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      if (isStaff) {
        // Load stats
        const statsData = await libraryAPI.getStats();
        setStats(statsData);

        // Fetch Tab-Specific Data
        if (activeTab === 'overview') {
          const rep = await libraryAPI.getReports({ type: 'summary' });
          setReportsData(rep);
        } else if (activeTab === 'catalog') {
          const bList = await libraryAPI.getBooks({ search: searchQuery, category: selectedCategory });
          setBooks(getResultsArray(bList));
          const cList = await libraryAPI.getCategories();
          setCategories(getResultsArray(cList));
        } else if (activeTab === 'issues') {
          const iList = await libraryAPI.getIssues({ active: true, search: searchQuery });
          setIssues(getResultsArray(iList));
        } else if (activeTab === 'overdue') {
          const oList = await libraryAPI.getOverdue();
          setOverdue(getResultsArray(oList));
        } else if (activeTab === 'fines') {
          const fList = await libraryAPI.getFines({ search: searchQuery });
          setFines(getResultsArray(fList));
        } else if (activeTab === 'reservations') {
          const rList = await libraryAPI.getReservations();
          setReservations(getResultsArray(rList));
        } else if (activeTab === 'digital') {
          const dList = await libraryAPI.getDigitalResources({ search: searchQuery });
          setDigitalResources(getResultsArray(dList));
        } else if (activeTab === 'journals') {
          const jList = await libraryAPI.getJournals({ search: searchQuery });
          setJournals(getResultsArray(jList));
        } else if (activeTab === 'reading-room') {
          const rooms = await libraryAPI.getReadingRooms();
          setReadingRooms(getResultsArray(rooms));
        } else if (activeTab === 'reports') {
          const repData = await libraryAPI.getReports({ type: reportType });
          setReportsData(repData);
        } else if (activeTab === 'settings') {
          const sett = await libraryAPI.getSettings();
          setSettings(sett);
        }
      } else {
        // Borrower Flow
        try {
          const membership = await libraryAPI.getMyMembership();
          setMyMembership(membership);
        } catch (err) {
          // If membership doesn't exist, create it
          if (err.response?.status === 404) {
            const newMem = await libraryAPI.createMyMembership();
            setMyMembership(newMem);
          }
        }

        if (activeTab === 'borrower-overview') {
          const myIss = await libraryAPI.getMyIssues();
          setMyIssues(getResultsArray(myIss).filter(i => i.status === 'ISSUED'));
          const myFn = await libraryAPI.getMyFines();
          setMyFines(getResultsArray(myFn).filter(f => f.status === 'PENDING'));
        } else if (activeTab === 'borrower-catalog') {
          const bList = await libraryAPI.getBooks({ search: searchQuery, category: selectedCategory });
          setBooks(getResultsArray(bList));
          const cList = await libraryAPI.getCategories();
          setCategories(getResultsArray(cList));
        } else if (activeTab === 'borrower-issues') {
          const myIss = await libraryAPI.getMyIssues();
          setMyIssues(getResultsArray(myIss));
        } else if (activeTab === 'borrower-fines') {
          const myFn = await libraryAPI.getMyFines();
          setMyFines(getResultsArray(myFn));
        } else if (activeTab === 'borrower-digital') {
          const dList = await libraryAPI.getDigitalResources({ search: searchQuery });
          setDigitalResources(getResultsArray(dList));
        } else if (activeTab === 'borrower-journals') {
          const jList = await libraryAPI.getJournals({ search: searchQuery });
          setJournals(getResultsArray(jList));
        } else if (activeTab === 'borrower-reading-room') {
          const rooms = await libraryAPI.getReadingRooms();
          setReadingRooms(getResultsArray(rooms));
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch data from library services.');
    } finally {
      setLoading(false);
    }
  };

  // Search Submit Handler
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchInitialData();
  };

  // Catalog Actions
  const handleOpenBookModal = async (book = null) => {
    if (book) {
      setCurrentBook(book);
      setBookForm({
        title: book.title || '', isbn: book.isbn || '', authors: book.authors || '',
        publisher: book.publisher || '', edition: book.edition || '',
        publication_year: book.publication_year || new Date().getFullYear(),
        description: book.description || '', language: book.language || 'EN',
        shelf_number: book.shelf_number || '', rack_number: book.rack_number || '',
        price: book.price || 0, total_copies: book.total_copies || 1,
        categories: book.categories?.map(c => c.id) || [],
        purchase_date: book.purchase_date || '', vendor: book.vendor || ''
      });
      setGeneratedBarcodes(book.copies?.map(c => c.barcode) || []);
    } else {
      setCurrentBook(null);
      setBookForm({
        title: '', isbn: '', authors: '', publisher: '', edition: '',
        publication_year: new Date().getFullYear(), description: '', language: 'EN',
        shelf_number: '', rack_number: '', price: 0, total_copies: 1, categories: [],
        purchase_date: '', vendor: ''
      });
      // Generate initial single barcode for total_copies=1
      try {
        const nextBcs = await libraryAPI.getNextBarcodes(1);
        setGeneratedBarcodes(nextBcs.next_barcodes || []);
      } catch (err) {
        setGeneratedBarcodes([]);
      }
    }
    setShowBookModal(true);
  };

  const getBarcodeDataURL = (text) => {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, text, {
      format: 'CODE128',
      width: 2,
      height: 40,
      displayValue: false
    });
    return canvas.toDataURL('image/jpeg');
  };

  const handlePrintBarcodes = (barcodesList, bookTitle) => {
    try {
      const doc = new jsPDF();
      let col = 0;
      let row = 0;
      const startX = 10;
      const startY = 15;
      const labelW = 60;
      const labelH = 30;
      const gapX = 5;
      const gapY = 5;

      barcodesList.forEach((bc, idx) => {
        if (idx > 0 && idx % 24 === 0) {
          doc.addPage();
          col = 0;
          row = 0;
        }

        const x = startX + col * (labelW + gapX);
        const y = startY + row * (labelH + gapY);

        doc.setDrawColor(200, 200, 200);
        doc.rect(x, y, labelW, labelH);

        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        const truncatedTitle = bookTitle.length > 30 ? bookTitle.substring(0, 27) + '...' : bookTitle;
        doc.text(truncatedTitle, x + 3, y + 5);

        try {
          const barcodeImg = getBarcodeDataURL(bc);
          doc.addImage(barcodeImg, 'JPEG', x + 5, y + 7, 50, 14);
        } catch (err) {
          console.error("Barcode generation error", err);
        }

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(bc, x + 15, y + 26);

        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(`Copy ${idx + 1}/${barcodesList.length}`, x + 40, y + 26);

        col++;
        if (col >= 3) {
          col = 0;
          row++;
        }
      });

      doc.save(`barcodes_${bookTitle.replace(/\s+/g, '_')}.pdf`);
      showAlert('Barcode labels PDF generated successfully!');
    } catch (e) {
      console.error(e);
      showAlert('Failed to generate barcode PDF.', false);
    }
  };

  const handleCopiesChange = async (copiesVal) => {
    const val = parseInt(copiesVal) || 1;
    setBookForm(prev => ({ ...prev, total_copies: val }));
    if (!currentBook && val > 0) {
      try {
        const nextBcs = await libraryAPI.getNextBarcodes(val);
        setGeneratedBarcodes(nextBcs.next_barcodes || []);
      } catch (err) {
        console.error("Error generating barcodes", err);
      }
    }
  };

  const handleBookSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...bookForm,
        category_ids: bookForm.categories,
        purchase_date: bookForm.purchase_date || null,
        barcodes: !currentBook ? generatedBarcodes : undefined
      };
      delete payload.categories;

      if (currentBook) {
        await libraryAPI.updateBook(currentBook.id, payload);
        showAlert('Book updated successfully!');
      } else {
        await libraryAPI.createBook(payload);
        showAlert('New book cataloged successfully!');
      }
      setShowBookModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert('Failed to save book. Check fields and try again.', false);
    }
  };

  const handleArchiveBook = async (bookId) => {
    if (await confirmDialog('Are you sure you want to archive this book? This will disable borrowing.')) {
      try {
        await libraryAPI.archiveBook(bookId);
        showAlert('Book archived.');
        fetchInitialData();
      } catch (err) {
        showAlert('Failed to archive book.', false);
      }
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      await libraryAPI.createCategory(categoryForm);
      showAlert('New Category created successfully!');
      setShowCategoryModal(false);
      setCategoryForm({ name: '', code: '', description: '', color: '#3b82f6' });
      // Refresh both categories list
      const cList = await libraryAPI.getCategories();
      setCategories(getResultsArray(cList));
    } catch (err) {
      showAlert('Failed to create category. Ensure name and code are unique.', false);
    }
  };

  // Issue & Return Actions
  const handleOpenIssueModal = (book = null) => {
    setSelectedBook(book || null);
    const availableCopy = book?.copies?.find(c => c.status === 'AVAILABLE');
    setIssueForm({
      member_id: '',
      copy_barcode: availableCopy?.barcode || (book ? book.accession_number : '') || '',
      issue_days: 14
    });
    setMemberSearchQuery('');
    setMatchedMembers([]);
    setSelectedMember(null);
    setShowMemberDropdown(false);
    setShowIssueModal(true);
  };

  const handleMemberSearchChange = (e) => {
    const value = e.target.value;
    setMemberSearchQuery(value);
    if (value === '') {
      setSelectedMember(null);
      setIssueForm(prev => ({ ...prev, member_id: '' }));
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setIssueForm(prev => ({ ...prev, member_id: member.id }));
    setMemberSearchQuery(`${member.user_name} (${member.membership_id})`);
    setShowMemberDropdown(false);
  };

  const handleIssueSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        member: selectedMember ? selectedMember.id : issueForm.member_id,
        copy_barcode: issueForm.copy_barcode,
        issue_days: issueForm.issue_days
      };
      await libraryAPI.issueBook(payload);
      showAlert('Book copy issued successfully!');
      setShowIssueModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to issue book copy.', false);
    }
  };

  const handleOpenReturnModal = (issue) => {
    // Calculate expected fine
    const daysOverdue = Math.max(0, Math.floor((new Date() - new Date(issue.due_date)) / (1000 * 60 * 60 * 24)));
    const fineRate = 5; // Default fallback
    const calculatedFine = daysOverdue * fineRate;

    setReturnForm({
      issue_id: issue.id,
      condition: 'GOOD',
      damage_notes: '',
      fine_amount: calculatedFine,
      waive_fine: false,
      bookTitle: issue.book_title,
      memberName: issue.member_name
    });
    setShowReturnModal(true);
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    try {
      const resp = await libraryAPI.returnBook(returnForm.issue_id, {
        condition_on_return: returnForm.condition,
        damage_notes: returnForm.damage_notes,
        waive_fine: returnForm.waive_fine
      });
      showAlert(resp.fine_amount > 0 && !returnForm.waive_fine 
        ? `Book returned. Overdue fine of ₹${resp.fine_amount} calculated.` 
        : 'Book returned successfully!');
      setShowReturnModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert('Failed to process book return.', false);
    }
  };

  // Fine Actions
  const handleOpenFineModal = (fine) => {
    setFineForm({
      fine_id: fine.id,
      payment_method: 'CASH',
      amount_paid: fine.amount - fine.paid_amount,
      transaction_reference: '',
      notes: '',
      totalAmount: fine.amount - fine.paid_amount
    });
    setShowFineModal(true);
  };

  const handleFineSubmit = async (e) => {
    e.preventDefault();
    try {
      await libraryAPI.payFine(fineForm.fine_id, {
        payment_method: fineForm.payment_method,
        amount_paid: fineForm.amount_paid,
        transaction_reference: fineForm.transaction_reference,
        notes: fineForm.notes
      });
      showAlert('Fine payment recorded successfully!');
      setShowFineModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert('Failed to record fine payment.', false);
    }
  };

  const handleWaiveFine = async (fineId) => {
    if (await confirmDialog('Are you sure you want to waive this fine?')) {
      try {
        await libraryAPI.waiveFine(fineId, { reason: 'Waived by Library Admin' });
        showAlert('Fine waived.');
        fetchInitialData();
      } catch (err) {
        showAlert('Failed to waive fine.', false);
      }
    }
  };

  // Reservations
  const handleCreateReservation = async (bookId) => {
    try {
      await libraryAPI.createReservation(bookId);
      showAlert('Book reserved successfully. You will be notified when available.');
      fetchInitialData();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to create reservation.', false);
    }
  };

  const handleCancelReservation = async (resId) => {
    if (await confirmDialog('Cancel this reservation?')) {
      try {
        await libraryAPI.cancelReservation(resId);
        showAlert('Reservation cancelled.');
        fetchInitialData();
      } catch (err) {
        showAlert('Failed to cancel reservation.', false);
      }
    }
  };

  // Digital Resources
  const handleDigitalSubmit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('title', digitalForm.title);
      fd.append('authors', digitalForm.authors);
      fd.append('resource_type', digitalForm.resource_type);
      fd.append('access_level', digitalForm.access_level);
      fd.append('description', digitalForm.description);
      fd.append('tags', digitalForm.tags);
      if (digitalForm.access_level === 'STUDENT') {
        fd.append('allowed_courses', JSON.stringify(digitalForm.allowed_courses || []));
      }
      if (digitalForm.file) {
        fd.append('file', digitalForm.file);
      } else if (digitalForm.external_url) {
        fd.append('external_url', digitalForm.external_url);
      }

      await libraryAPI.uploadDigitalResource(fd);
      showAlert('Digital resource uploaded successfully!');
      setShowDigitalModal(false);
      setDigitalForm({
        title: '', authors: '', resource_type: 'EBOOK', access_level: 'ALL',
        allowed_courses: [], external_url: '', file: null, description: '', tags: ''
      });
      fetchInitialData();
    } catch (err) {
      console.error('Upload error detail:', err.response?.data);
      const errMsg = err.response?.data ? JSON.stringify(err.response.data) : 'Failed to upload digital resource.';
      showAlert(errMsg, false);
    }
  };

  const handleDeleteDigital = async (resId) => {
    if (await confirmDialog('Delete this digital resource permanently?')) {
      try {
        await libraryAPI.deleteDigitalResource(resId);
        showAlert('Digital resource deleted.');
        fetchInitialData();
      } catch (err) {
        showAlert('Failed to delete resource.', false);
      }
    }
  };

  // Journals
  const handleOpenJournalModal = (journal = null) => {
    if (journal) {
      setCurrentJournal(journal);
      setJournalForm({
        name: journal.name, issn: journal.issn, publisher: journal.publisher,
        frequency: journal.frequency, journal_type: journal.journal_type,
        subscription_start: journal.subscription_start, subscription_end: journal.subscription_end,
        subscription_cost: journal.subscription_cost, status: journal.status,
        digital_url: journal.digital_url
      });
    } else {
      setCurrentJournal(null);
      setJournalForm({
        name: '', issn: '', publisher: '', frequency: 'Monthly',
        journal_type: 'PRINT', subscription_start: '', subscription_end: '',
        subscription_cost: 0, status: 'ACTIVE', digital_url: ''
      });
    }
    setShowJournalModal(true);
  };

  const handleJournalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentJournal) {
        await libraryAPI.updateJournal(currentJournal.id, journalForm);
        showAlert('Journal updated successfully!');
      } else {
        await libraryAPI.createJournal(journalForm);
        showAlert('Journal subscription added!');
      }
      setShowJournalModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert('Failed to save journal subscription.', false);
    }
  };

  const handleDeleteJournal = async (journalId) => {
    if (await confirmDialog('Cancel/Delete this journal subscription?')) {
      try {
        await libraryAPI.deleteJournal(journalId);
        showAlert('Journal subscription cancelled.');
        fetchInitialData();
      } catch (err) {
        showAlert('Failed to delete subscription.', false);
      }
    }
  };

  // Reading Room Log entry
  const handleOpenRoomEntryModal = (room) => {
    setRoomEntryForm({
      room_id: room.id,
      member_id: '',
      seat_number: ''
    });
    setShowRoomEntryModal(true);
  };

  const handleRoomEntrySubmit = async (e) => {
    e.preventDefault();
    try {
      await libraryAPI.logRoomEntry(roomEntryForm);
      showAlert('Reading room occupancy logged!');
      setShowRoomEntryModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Failed to log room entry.', false);
    }
  };

  // Settings Actions
  const handleOpenSettingsModal = () => {
    // Populate form with current settings keys or defaults
    setSettingsForm({
      fine_rate_student: settings.find(s => s.key === 'FINE_RATE_STUDENT')?.value || 5,
      fine_rate_faculty: settings.find(s => s.key === 'FINE_RATE_FACULTY')?.value || 10,
      duration_student: settings.find(s => s.key === 'DURATION_STUDENT')?.value || 14,
      duration_faculty: settings.find(s => s.key === 'DURATION_FACULTY')?.value || 30,
      max_books_student: settings.find(s => s.key === 'MAX_BOOKS_STUDENT')?.value || 3,
      max_books_faculty: settings.find(s => s.key === 'MAX_BOOKS_FACULTY')?.value || 5
    });
    setShowSettingsModal(true);
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      await libraryAPI.saveSettings(settingsForm);
      showAlert('Library policies and configurations updated successfully!');
      setShowSettingsModal(false);
      fetchInitialData();
    } catch (err) {
      showAlert('Failed to save settings.', false);
    }
  };

  const generatePDFReport = async (type) => {
    try {
      setLoading(true);
      let reportTitle = '';
      let headers = [];
      let rows = [];
      let columnStyles = {};

      if (type === 'circulation') {
        reportTitle = 'Library Circulation Report';
        const data = await libraryAPI.getReports({ type: 'summary' });
        headers = [['Metric', 'Value']];
        rows = [
          ['Issues This Month', data.issues_this_month || 0],
          ['Returns This Month', data.returns_this_month || 0],
          ['Fines Collected This Month', `Rs. ${data.fines_collected_this_month || 0}`],
          ['Total Registered Members', data.total_members || 0],
          ['Total Cataloged Books', data.total_books || 0],
        ];
        columnStyles = {
          1: { halign: 'right' }
        };
      } else if (type === 'overdue') {
        reportTitle = 'Overdue Books Tracker';
        const data = await libraryAPI.getOverdue();
        const list = getResultsArray(data);
        headers = [['Book Title', 'Borrower', 'Copy Barcode', 'Due Date', 'Days Overdue', 'Est. Fine']];
        rows = list.map(item => [
          item.book_title,
          item.member_name,
          item.copy_barcode,
          new Date(item.due_date).toLocaleDateString(),
          `${Math.max(0, Math.floor((new Date() - new Date(item.due_date)) / (1000 * 60 * 60 * 24)))} days`,
          `Rs. ${item.estimated_fine}`
        ]);
        columnStyles = {
          4: { halign: 'center' },
          5: { halign: 'right' }
        };
      } else if (type === 'fines') {
        reportTitle = 'Fines & Fee Ledger';
        const data = await libraryAPI.getFines();
        const list = getResultsArray(data);
        headers = [['Member ID & Name', 'Fine Type', 'Amount', 'Paid', 'Status', 'Paid Date']];
        rows = list.map(f => [
          `${f.member_name} (ID: ${f.member_id})`,
          f.fine_type,
          `Rs. ${f.amount}`,
          `Rs. ${f.paid_amount}`,
          f.status,
          f.paid_at ? new Date(f.paid_at).toLocaleDateString() : 'N/A'
        ]);
        columnStyles = {
          2: { halign: 'right' },
          3: { halign: 'right' },
          4: { halign: 'center' }
        };
      } else if (type === 'inventory') {
        reportTitle = 'Book Inventory Catalog';
        const data = await libraryAPI.getBooks();
        const list = getResultsArray(data);
        headers = [['Accession No.', 'Book Title', 'Authors', 'Publisher', 'Shelf Location', 'Total Copies', 'Available']];
        rows = list.map(b => [
          b.accession_number,
          b.title,
          b.authors,
          b.publisher,
          b.shelf_number || 'N/A',
          b.total_copies,
          b.available_copies
        ]);
        columnStyles = {
          0: { halign: 'center' },
          5: { halign: 'center' },
          6: { halign: 'center' }
        };
      }

      const doc = new jsPDF();
      
      // Top accent bar
      doc.setFillColor(37, 99, 235); // primary blue
      doc.rect(0, 0, 210, 4, 'F');
      
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 23, 42); // slate 900
      doc.text("MEDICAL COLLEGE CENTRAL LIBRARY", 14, 18);
      
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(71, 85, 105); // slate 600
      doc.text(reportTitle, 14, 25);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(148, 163, 184); // slate 400
      doc.text(`Generated On: ${new Date().toLocaleDateString('en-IN')} ${new Date().toLocaleTimeString('en-IN')}`, 14, 31);
      
      doc.setDrawColor(241, 245, 249); // slate 100
      doc.line(14, 35, 196, 35);

      autoTable(doc, {
        startY: 40,
        head: headers,
        body: rows,
        theme: 'grid',
        headStyles: { 
          fillColor: [30, 41, 59], // Slate 800
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold'
        },
        styles: { 
          fontSize: 8.5, 
          cellPadding: 5,
          valign: 'middle'
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252] // Slate 50
        },
        margin: { left: 14, right: 14 },
        columnStyles: columnStyles,
        didDrawPage: (data) => {
          // Page footer
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184); // Slate 400
          doc.text(`Page ${doc.internal.getNumberOfPages()}`, 196, doc.internal.pageSize.height - 10, { align: 'right' });
          doc.text("Medical College Central Library — Confidential System Report", 14, doc.internal.pageSize.height - 10);
        }
      });

      doc.save(`Library_${type}_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
      showAlert(`${reportTitle} PDF downloaded successfully!`);
    } catch (e) {
      console.error(e);
      showAlert('Failed to generate report PDF.', false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Library Management System">
      <div className="dashboard-container">
        {/* Back to main dashboard link */}
        <div style={{ marginBottom: '20px' }}>
          <button 
            onClick={handleGoBack}
            className="btn-sm btn-outline"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              fontWeight: '600',
              borderRadius: '8px',
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              color: '#334155',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#94a3b8';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
          >
            ← Back to Main Dashboard
          </button>
        </div>

        {/* Alerts — fixed above any open modal (zIndex 1000) so feedback is never hidden behind one */}
        {success && (
          <div style={{
            position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100,
            background: '#d1fae5', borderLeft: '4px solid #10b981', color: '#065f46',
            padding: '16px', borderRadius: '8px', fontWeight: '500', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
          }}>
            {success}
          </div>
        )}
        {error && (
          <div style={{
            position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 1100,
            background: '#fee2e2', borderLeft: '4px solid #ef4444', color: '#991b1b',
            padding: '16px', borderRadius: '8px', fontWeight: '500', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
          }}>
            {error}
          </div>
        )}

        {/* STATS HEADER CARDS */}
        {isStaff ? (
          <div className="stats-grid">
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>📚</span>}
              title="Total Books"
              value={stats.total_books}
              color="blue"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>🔄</span>}
              title="Active Issues"
              value={stats.issued_books}
              color="green"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>⏰</span>}
              title="Overdue Copies"
              value={stats.overdue_books}
              color="red"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>👥</span>}
              title="Active Members"
              value={stats.active_members}
              color="purple"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>💰</span>}
              title="Outstanding Fines"
              value={`₹${stats.pending_fines_amount || 0}`}
              color="orange"
            />
          </div>
        ) : (
          <div className="stats-grid">
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>🪪</span>}
              title="Membership ID"
              value={myMembership?.membership_id || 'Generating...'}
              color="blue"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>🔄</span>}
              title="My Borrowed Books"
              value={myMembership?.books_currently_issued || 0}
              color="green"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>💰</span>}
              title="My Outstanding Fines"
              value={`₹${myMembership?.outstanding_fines || 0}`}
              color="red"
            />
            <StatCard
              icon={<span style={{ fontSize: '24px' }}>⏰</span>}
              title="Membership Status"
              value={myMembership?.status || 'ACTIVE'}
              color="purple"
            />
          </div>
        )}

        {/* TABS CONTAINER */}
        <div className="tabs-container">
          <div className="tabs">
            {isStaff ? (
              <>
                <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>📊 Dashboard</button>
                <button className={`tab ${activeTab === 'catalog' ? 'active' : ''}`} onClick={() => setActiveTab('catalog')}>📚 Book Catalog</button>
                <button className={`tab ${activeTab === 'issues' ? 'active' : ''}`} onClick={() => setActiveTab('issues')}>🔄 Issue & Return</button>
                <button className={`tab ${activeTab === 'overdue' ? 'active' : ''}`} onClick={() => setActiveTab('overdue')}>⏰ Overdue Tracker</button>
                <button className={`tab ${activeTab === 'fines' ? 'active' : ''}`} onClick={() => setActiveTab('fines')}>💰 Fines Ledger</button>
                <button className={`tab ${activeTab === 'digital' ? 'active' : ''}`} onClick={() => setActiveTab('digital')}>📱 Digital Library</button>
                
                <button className={`tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>📈 Reports & Export</button>
                <button className={`tab ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>⚙️ Library Settings</button>
                <button className={`tab ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>📅 Calendar</button>
              </>
            ) : (
              <>
                <button className={`tab ${activeTab === 'borrower-overview' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-overview')}>📊 My Account</button>
                <button className={`tab ${activeTab === 'borrower-catalog' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-catalog')}>📚 Search Catalog</button>
                <button className={`tab ${activeTab === 'borrower-issues' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-issues')}>🔄 Borrowing History</button>
                <button className={`tab ${activeTab === 'borrower-fines' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-fines')}>💰 My Fines</button>
                <button className={`tab ${activeTab === 'borrower-digital' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-digital')}>📱 Digital Resources</button>
                <button className={`tab ${activeTab === 'borrower-journals' ? 'active' : ''}`} onClick={() => setActiveTab('borrower-journals')}>📰 Medical Journals</button>
                <button className={`tab ${activeTab === 'calendar' ? 'active' : ''}`} onClick={() => setActiveTab('calendar')}>📅 Calendar</button>
              </>
            )}
          </div>
        </div>

        {/* TAB CONTENTS */}
        <div className="tab-content">
          {loading && <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#6b7280' }}>🔄 Loading library records...</div>}

          {!loading && (
            <>
              {/* LIBRARIAN TABS */}
              {activeTab === 'overview' && (
                <div className="grid-2-col">
                  <div className="card">
                    <div className="card-header">
                      <h2>Monthly Circulation Summary</h2>
                    </div>
                    <div className="card-body">
                      <div className="stats-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                          <span style={{ fontWeight: '500' }}>Books Checked Out</span>
                          <span className="badge badge-blue">{reportsData?.issues_this_month || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                          <span style={{ fontWeight: '500' }}>Books Safely Returned</span>
                          <span className="badge badge-green">{reportsData?.returns_this_month || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f3f4f6', paddingBottom: '12px' }}>
                          <span style={{ fontWeight: '500' }}>Fines Collected</span>
                          <span className="badge badge-purple">₹{reportsData?.fines_collected_this_month || 0}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '4px' }}>
                          <span style={{ fontWeight: '500' }}>Total Registered Library Members</span>
                          <span className="badge badge-orange">{reportsData?.total_members || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header">
                      <h2>Most Popular Books This Month</h2>
                    </div>
                    <div className="card-body">
                      <div className="activity-list">
                        {(reportsData.top_books && reportsData.top_books.length > 0) ? (
                          reportsData.top_books.map((item, idx) => (
                            <div key={idx} className="list-item">
                              <span style={{ fontWeight: '700', fontSize: '18px', color: '#4f46e5', minWidth: '30px' }}>#{idx+1}</span>
                              <div className="list-item-content">
                                <h4>{item.title}</h4>
                                <p>{item.authors} • Issued {item.issue_count} times</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ color: '#6b7280', fontSize: '14px' }}>No issue transactions found for top popularity ranking.</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'catalog' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Master Catalog Management</h2>
                    <div className="card-header-actions">
                      <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Search Title, Author, ISBN..."
                          className="search-input"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <select
                          className="filter-select"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button type="submit" className="btn-primary">Search</button>
                      </form>
                      <button onClick={() => setShowCategoryModal(true)} className="btn-primary" style={{ background: '#4f46e5' }}>+ Add Category</button>
                      <button onClick={() => handleOpenBookModal()} className="btn-primary" style={{ background: '#10b981' }}>+ Add New Book</button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Author(s)</th>
                            <th>ISBN & Shelf</th>
                            <th>Categories</th>
                            <th>Available Copies</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {books.map(b => (
                            <tr key={b.id}>
                              <td>
                                <strong style={{ color: '#1f2937' }}>{b.title}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Acc No: {b.accession_number}</div>
                              </td>
                              <td>{b.authors}</td>
                              <td>
                                <div>{b.isbn}</div>
                                <div style={{ fontSize: '12px', color: '#4b5563' }}>Shelf: {b.shelf_number || 'N/A'} (Rack {b.rack_number || 'N/A'})</div>
                              </td>
                              <td>
                                {b.categories?.map(c => (
                                  <span key={c.id} className="badge badge-blue" style={{ marginRight: '4px' }}>{c.name}</span>
                                ))}
                              </td>
                              <td>
                                <span className={`badge ${b.available_copies > 0 ? 'badge-green' : 'badge-red'}`}>
                                  {b.available_copies} of {b.total_copies}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${b.is_active ? 'badge-green' : 'badge-gray'}`}>
                                  {b.is_active ? 'Active' : 'Archived'}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button onClick={() => handleOpenBookModal(b)} className="btn-sm btn-outline">Edit</button>
                                  <button onClick={() => handleOpenIssueModal(b)} className="btn-sm btn-success" disabled={b.available_copies === 0}>Issue</button>
                                  <button onClick={() => handleArchiveBook(b.id)} className="btn-sm btn-danger">Archive</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'issues' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Active Book Circulation Issues</h2>
                    <div className="card-header-actions">
                      <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Search Member ID or Barcode..."
                          className="search-input"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <button type="submit" className="btn-primary">Find</button>
                      </form>
                      <button onClick={() => handleOpenIssueModal()} className="btn-primary" style={{ background: '#4f46e5' }}>+ Issue Book Copy</button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title & Copy Barcode</th>
                            <th>Member ID & Name</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {issues.map(i => (
                            <tr key={i.id}>
                              <td>
                                <strong>{i.book_title}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Barcode: {i.copy_barcode}</div>
                              </td>
                              <td>
                                <strong>{i.member_name}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {i.member_id}</div>
                              </td>
                              <td>{new Date(i.issue_date).toLocaleDateString()}</td>
                              <td>{new Date(i.due_date).toLocaleDateString()}</td>
                              <td>
                                <span className={`badge ${i.is_overdue ? 'badge-red' : 'badge-green'}`}>
                                  {i.is_overdue ? 'Overdue' : 'Active'}
                                </span>
                              </td>
                              <td>
                                <button onClick={() => handleOpenReturnModal(i)} className="btn-sm btn-primary">Process Return</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'overdue' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Overdue & Fine Enforcement Tracker</h2>
                    <button onClick={() => showAlert('Automated email reminders sent to all overdue borrowers.')} className="btn-danger">Send Reminders to All Overdue</button>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Borrower Details</th>
                            <th>Due Date</th>
                            <th>Days Overdue</th>
                            <th>Pending Fine (Est.)</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overdue.map(o => (
                            <tr key={o.id}>
                              <td>
                                <strong>{o.book_title}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Barcode: {o.copy_barcode}</div>
                              </td>
                              <td>
                                <strong>{o.member_name}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {o.member_id} • {o.member_phone || 'No Phone'}</div>
                              </td>
                              <td>{new Date(o.due_date).toLocaleDateString()}</td>
                              <td>
                                <span className="badge badge-red">{o.days_overdue} days</span>
                              </td>
                              <td><strong>₹{o.estimated_fine}</strong></td>
                              <td>
                                <div className="action-buttons">
                                  <button onClick={() => showAlert(`Reminder email sent to ${o.member_name}`)} className="btn-sm btn-outline">Send Reminder</button>
                                  <button onClick={() => handleOpenReturnModal(o)} className="btn-sm btn-primary">Return</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'fines' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Fines & Fee Ledger</h2>
                    <input
                      type="text"
                      placeholder="Search Member ID..."
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Member ID & Name</th>
                            <th>Fine Type</th>
                            <th>Total Fine</th>
                            <th>Paid Amount</th>
                            <th>Status</th>
                            <th>Payment Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fines.map(f => (
                            <tr key={f.id}>
                              <td>
                                <strong>{f.member_name}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>ID: {f.member_id}</div>
                              </td>
                              <td>
                                <span className={`badge ${f.fine_type === 'DAMAGE' ? 'badge-orange' : f.fine_type === 'LOST' ? 'badge-red' : 'badge-yellow'}`}>
                                  {f.fine_type}
                                </span>
                              </td>
                              <td><strong>₹{f.amount}</strong></td>
                              <td>₹{f.paid_amount}</td>
                              <td>
                                <span className={`badge ${f.status === 'PAID' ? 'badge-green' : f.status === 'WAIVED' ? 'badge-gray' : 'badge-red'}`}>
                                  {f.status}
                                </span>
                              </td>
                              <td>{f.paid_at ? new Date(f.paid_at).toLocaleDateString() : 'N/A'}</td>
                              <td>
                                {f.status === 'PENDING' && (
                                  <div className="action-buttons">
                                    <button onClick={() => handleOpenFineModal(f)} className="btn-sm btn-success">Pay Fine</button>
                                    <button onClick={() => handleWaiveFine(f.id)} className="btn-sm btn-outline">Waive</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}



              {activeTab === 'digital' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Digital Library Resources (PDFs & E-Books)</h2>
                    <button onClick={() => setShowDigitalModal(true)} className="btn-primary" style={{ background: '#10b981' }}>+ Upload Digital Resource</button>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Resource Title</th>
                            <th>Type</th>
                            <th>Access Level</th>
                            <th>Downloads</th>
                            <th>Upload Date</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {digitalResources.map(d => (
                            <tr key={d.id}>
                              <td>
                                <strong>{d.title}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Authors: {d.authors}</div>
                              </td>
                              <td>
                                <span className="badge badge-purple">{d.resource_type}</span>
                              </td>
                              <td>
                                <span className="badge badge-blue">{d.access_level}</span>
                                {d.access_level === 'STUDENT' && d.allowed_courses && d.allowed_courses.length > 0 && (
                                  <div style={{ fontSize: '11px', color: '#4b5563', marginTop: '4px', fontStyle: 'italic' }}>
                                    Allowed: {d.allowed_courses.join(', ')}
                                  </div>
                                )}
                              </td>
                              <td>{d.download_count} downloads</td>
                              <td>{new Date(d.created_at).toLocaleDateString()}</td>
                              <td>
                                <div className="action-buttons">
                                  {d.file && <a href={getFileUrl(d.file)} download target="_blank" rel="noopener noreferrer" className="btn-sm btn-outline" style={{ textDecoration: 'none' }}>Download</a>}
                                  {d.external_url && <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="btn-sm btn-outline" style={{ textDecoration: 'none' }}>Open Link</a>}
                                  <button onClick={() => handleDeleteDigital(d.id)} className="btn-sm btn-danger">Delete</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'journals' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Medical Journals Subscription Tracker</h2>
                    <button onClick={() => handleOpenJournalModal()} className="btn-primary" style={{ background: '#10b981' }}>+ New Journal Subscription</button>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Journal Name</th>
                            <th>ISSN & Publisher</th>
                            <th>Frequency & Type</th>
                            <th>Subscription Period</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journals.map(j => (
                            <tr key={j.id}>
                              <td><strong>{j.name}</strong></td>
                              <td>
                                <div>ISSN: {j.issn}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Publisher: {j.publisher}</div>
                              </td>
                              <td>
                                <div>{j.frequency}</div>
                                <span className={`badge ${j.journal_type === 'DIGITAL' ? 'badge-blue' : 'badge-orange'}`}>{j.journal_type}</span>
                              </td>
                              <td>
                                <div style={{ fontSize: '12px' }}>Start: {new Date(j.subscription_start).toLocaleDateString()}</div>
                                <div style={{ fontSize: '12px' }}>End: {new Date(j.subscription_end).toLocaleDateString()}</div>
                              </td>
                              <td>
                                <span className={`badge ${j.status === 'ACTIVE' ? 'badge-green' : 'badge-red'}`}>
                                  {j.status}
                                </span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button onClick={() => handleOpenJournalModal(j)} className="btn-sm btn-outline">Edit</button>
                                  {j.digital_url && <a href={j.digital_url} target="_blank" rel="noopener noreferrer" className="btn-sm btn-success" style={{ textDecoration: 'none' }}>Access Portal</a>}
                                  <button onClick={() => handleDeleteJournal(j.id)} className="btn-sm btn-danger">Cancel</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'reports' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Export Library System Reports</h2>
                  </div>
                  <div className="card-body">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                        <h3>Circulation Report</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Overview of books issued and returned.</p>
                        <button onClick={() => generatePDFReport('circulation')} className="btn-primary">Download PDF Report</button>
                      </div>

                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                        <h3>Overdue Books Report</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>List of current outstanding overdues.</p>
                        <button onClick={() => generatePDFReport('overdue')} className="btn-danger">Download PDF Report</button>
                      </div>

                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                        <h3>Fines Ledger</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Statement of paid, unpaid and waived fines.</p>
                        <button onClick={() => generatePDFReport('fines')} className="btn-primary">Download PDF Report</button>
                      </div>

                      <div style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                        <h3>Book Inventory</h3>
                        <p style={{ color: '#6b7280', fontSize: '14px' }}>Catalog list containing shelving indices.</p>
                        <button onClick={() => generatePDFReport('inventory')} className="btn-primary">Download PDF Report</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Library Regulations Settings</h2>
                  </div>
                  <div className="card-body">
                    <button onClick={handleOpenSettingsModal} className="btn-primary">Edit Borrowing Rules & Fine Rates</button>
                    <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>Student Overdue Fine Rate</strong>
                        <span>₹5 per day</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>Faculty Overdue Fine Rate</strong>
                        <span>₹10 per day</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>Student Borrow Duration</strong>
                        <span>14 Days</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', borderBottom: '1px solid #f3f4f6' }}>
                        <strong>Faculty Borrow Duration</strong>
                        <span>30 Days</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px' }}>
                        <strong>Max Allowed Books (Student)</strong>
                        <span>3 Books</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* BORROWER TABS */}
              {activeTab === 'borrower-overview' && (
                <div className="grid-2-col">
                  <div className="card">
                    <div className="card-header">
                      <h2>My Active Book Loans</h2>
                    </div>
                    <div className="card-body">
                      {myIssues.length > 0 ? (
                        <div className="activity-list">
                          {myIssues.map(i => (
                            <div key={i.id} className="list-item" style={{ borderLeft: '4px solid #10b981', paddingLeft: '12px' }}>
                              <div className="list-item-content">
                                <h4>{i.book_title}</h4>
                                <p>Barcode: {i.copy_barcode} • Due: {new Date(i.due_date).toLocaleDateString()}</p>
                              </div>
                              <span className={`badge ${i.is_overdue ? 'badge-red' : 'badge-green'}`}>
                                {i.is_overdue ? 'Overdue' : 'Active'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>No active book loans. Search the catalog to reserve a book.</div>
                      )}
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header">
                      <h2>My Outstanding Fines</h2>
                    </div>
                    <div className="card-body">
                      {myFines.length > 0 ? (
                        <div className="activity-list">
                          {myFines.map(f => (
                            <div key={f.id} className="list-item" style={{ borderLeft: '4px solid #ef4444', paddingLeft: '12px' }}>
                              <div className="list-item-content">
                                <h4>{f.fine_type} Fine</h4>
                                <p>Reason: {f.notes || 'Overdue return penalty'}</p>
                              </div>
                              <strong style={{ color: '#dc2626' }}>₹{f.amount}</strong>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ color: '#6b7280', textAlign: 'center', padding: '24px 0' }}>Nice! You have no outstanding fines.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'borrower-catalog' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Search Medical Library Books</h2>
                    <div className="card-header-actions">
                      <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          placeholder="Search Title, Author, ISBN..."
                          className="search-input"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <select
                          className="filter-select"
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                          <option value="">All Categories</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button type="submit" className="btn-primary">Search</button>
                      </form>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Author(s)</th>
                            <th>ISBN & Shelf</th>
                            <th>Categories</th>
                            <th>Availability Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {books.map(b => (
                            <tr key={b.id}>
                              <td>
                                <strong>{b.title}</strong>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Edition: {b.edition || '1st'}</div>
                              </td>
                              <td>{b.authors}</td>
                              <td>
                                <div>{b.isbn}</div>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Shelf: {b.shelf_number || 'N/A'}</div>
                              </td>
                              <td>
                                {b.categories?.map(c => (
                                  <span key={c.id} className="badge badge-blue" style={{ marginRight: '4px' }}>{c.name}</span>
                                ))}
                              </td>
                              <td>
                                <span className={`badge ${b.available_copies > 0 ? 'badge-green' : 'badge-red'}`}>
                                  {b.available_copies > 0 ? `${b.available_copies} Copies Available` : 'All Out of Stock'}
                                </span>
                              </td>
                              <td>
                                {b.available_copies > 0 ? (
                                  <button onClick={() => showAlert(`Visit the library reception to checkout. Book copy status is available on shelf ${b.shelf_number || 'N/A'}`)} className="btn-sm btn-success">Available</button>
                                ) : (
                                  <button onClick={() => handleCreateReservation(b.id)} className="btn-sm btn-primary">Reserve Book</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'borrower-issues' && (
                <div className="card">
                  <div className="card-header">
                    <h2>My Complete Circulation History</h2>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Copy Barcode</th>
                            <th>Issue Date</th>
                            <th>Due Date</th>
                            <th>Return Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myIssues.map(i => (
                            <tr key={i.id}>
                              <td><strong>{i.book_title}</strong></td>
                              <td>{i.copy_barcode}</td>
                              <td>{new Date(i.issue_date).toLocaleDateString()}</td>
                              <td>{new Date(i.due_date).toLocaleDateString()}</td>
                              <td>{i.return_date ? new Date(i.return_date).toLocaleDateString() : 'Active'}</td>
                              <td>
                                <span className={`badge ${i.status === 'RETURNED' ? 'badge-green' : i.is_overdue ? 'badge-red' : 'badge-blue'}`}>
                                  {i.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'borrower-fines' && (
                <div className="card">
                  <div className="card-header">
                    <h2>My Fines Ledger</h2>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Book Title</th>
                            <th>Fine Type</th>
                            <th>Amount</th>
                            <th>Paid Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {myFines.map(f => (
                            <tr key={f.id}>
                              <td><strong>{f.book_title}</strong></td>
                              <td>{f.fine_type}</td>
                              <td><strong>₹{f.amount}</strong></td>
                              <td>₹{f.paid_amount}</td>
                              <td>
                                <span className={`badge ${f.status === 'PAID' ? 'badge-green' : f.status === 'WAIVED' ? 'badge-gray' : 'badge-red'}`}>
                                  {f.status}
                                </span>
                              </td>
                              <td>
                                {f.status === 'PENDING' && (
                                  <button onClick={() => showAlert('Payment integration simulation: Contact the librarian to pay this fine.', true)} className="btn-sm btn-primary">Pay Online</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'borrower-digital' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Browse E-Books & Medical PDFs</h2>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Authors</th>
                            <th>Format</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {digitalResources.map(d => (
                            <tr key={d.id}>
                              <td><strong>{d.title}</strong></td>
                              <td>{d.authors}</td>
                              <td><span className="badge badge-blue">{d.resource_type}</span></td>
                              <td>{d.description || 'No description available'}</td>
                              <td>
                                {d.file ? (
                                  <a href={getFileUrl(d.file)} download target="_blank" rel="noopener noreferrer" className="btn-sm btn-primary" style={{ textDecoration: 'none' }}>Download PDF</a>
                                ) : (
                                  <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="btn-sm btn-outline" style={{ textDecoration: 'none' }}>Access Resource</a>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'borrower-journals' && (
                <div className="card">
                  <div className="card-header">
                    <h2>Subscribed Medical Journals</h2>
                  </div>
                  <div className="card-body">
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Journal Name</th>
                            <th>Publisher & ISSN</th>
                            <th>Frequency</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {journals.map(j => (
                            <tr key={j.id}>
                              <td><strong>{j.name}</strong></td>
                              <td>{j.publisher} (ISSN: {j.issn})</td>
                              <td>{j.frequency}</td>
                              <td>
                                {j.journal_type === 'DIGITAL' && j.digital_url ? (
                                  <a href={j.digital_url} target="_blank" rel="noopener noreferrer" className="btn-sm btn-primary" style={{ textDecoration: 'none' }}>Read Online</a>
                                ) : (
                                  <span className="badge badge-orange">Available in Library Library Stacks</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'calendar' && (
                <div className="card" style={{ padding: 0, overflow: 'visible' }}>
                  <Calendar />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* POPUP MODALS AND FORMS */}
      {/* 1. Catalog Book Modal */}
      {showBookModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '28px', borderRadius: '12px', width: '600px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>
              {currentBook ? '✏️ Edit Cataloged Book' : '📚 Catalog New Book'}
            </h2>
            <form onSubmit={handleBookSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Book Title *</label>
                  <input type="text" required className="search-input w-full" value={bookForm.title} onChange={e => setBookForm({...bookForm, title: e.target.value})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Author(s) *</label>
                  <input type="text" required className="search-input w-full" value={bookForm.authors} onChange={e => setBookForm({...bookForm, authors: e.target.value})} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>ISBN Code *</label>
                  <input type="text" required className="search-input w-full" value={bookForm.isbn} onChange={e => setBookForm({...bookForm, isbn: e.target.value})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Publisher</label>
                  <input type="text" className="search-input w-full" value={bookForm.publisher} onChange={e => setBookForm({...bookForm, publisher: e.target.value})} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Edition</label>
                  <input type="text" className="search-input w-full" value={bookForm.edition} onChange={e => setBookForm({...bookForm, edition: e.target.value})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Publication Year</label>
                  <input type="number" className="search-input w-full" value={bookForm.publication_year} onChange={e => setBookForm({...bookForm, publication_year: parseInt(e.target.value) || 2026})} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Shelf Location</label>
                  <input type="text" placeholder="e.g. Shelf A-12" className="search-input w-full" value={bookForm.shelf_number} onChange={e => setBookForm({...bookForm, shelf_number: e.target.value})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Rack Location</label>
                  <input type="text" placeholder="e.g. Rack 4" className="search-input w-full" value={bookForm.rack_number} onChange={e => setBookForm({...bookForm, rack_number: e.target.value})} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Purchase Date</label>
                  <input type="date" className="search-input w-full" value={bookForm.purchase_date} onChange={e => setBookForm({...bookForm, purchase_date: e.target.value})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Vendor / Supplier</label>
                  <input type="text" className="search-input w-full" value={bookForm.vendor} onChange={e => setBookForm({...bookForm, vendor: e.target.value})} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Price (₹)</label>
                  <input type="number" className="search-input w-full" value={bookForm.price} onChange={e => setBookForm({...bookForm, price: parseFloat(e.target.value) || 0})} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Number of Copies</label>
                  {currentBook ? (
                    <input type="number" disabled className="search-input w-full bg-gray-100 cursor-not-allowed" value={bookForm.total_copies} />
                  ) : (
                    <input type="number" min="1" className="search-input w-full" value={bookForm.total_copies} onChange={e => handleCopiesChange(e.target.value)} />
                  )}
                </div>
              </div>

              {/* Barcode Pre-generation Preview Section */}
              {generatedBarcodes.length > 0 && (
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', marginTop: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>
                      🏷️ Generated Copy Barcodes ({generatedBarcodes.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => handlePrintBarcodes(generatedBarcodes, bookForm.title || 'Book Barcodes')}
                      className="btn-sm btn-success"
                      style={{ padding: '4px 10px', fontSize: '12px' }}
                    >
                      🖨️ Print Barcode Labels
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '90px', overflowY: 'auto' }}>
                    {generatedBarcodes.map((bc, idx) => (
                      <span key={idx} style={{ background: '#3b82f6', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', fontFamily: 'monospace' }}>
                        {bc}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Description</label>
                <textarea placeholder="Write summary or indexing keywords..." className="search-input w-full" value={bookForm.description} onChange={e => setBookForm({...bookForm, description: e.target.value})} style={{ minHeight: '80px' }} />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Language</label>
                  <select className="filter-select w-full" value={bookForm.language} onChange={e => setBookForm({...bookForm, language: e.target.value})}>
                    <option value="EN">English</option>
                    <option value="HI">Hindi</option>
                    <option value="MR">Marathi</option>
                    <option value="TA">Tamil</option>
                    <option value="TE">Telugu</option>
                    <option value="OT">Other</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Book Category Tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '100px', overflowY: 'auto', border: '1px solid #e5e7eb', padding: '8px', borderRadius: '6px' }}>
                  {categories.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={bookForm.categories?.includes(c.id)}
                        onChange={(e) => {
                          const updated = e.target.checked
                            ? [...(bookForm.categories || []), c.id]
                            : (bookForm.categories || []).filter(id => id !== c.id);
                          setBookForm({...bookForm, categories: updated});
                        }}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" onClick={() => setShowBookModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Save Book</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h2>Create New Category</h2>
            <form onSubmit={handleCategorySubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="text"
                placeholder="Category Name (e.g. Anatomy)"
                required
                className="search-input w-full"
                value={categoryForm.name}
                onChange={e => setCategoryForm({...categoryForm, name: e.target.value})}
              />
              <input
                type="text"
                placeholder="Code (e.g. ANA)"
                required
                className="search-input w-full"
                value={categoryForm.code}
                onChange={e => setCategoryForm({...categoryForm, code: e.target.value})}
              />
              <input
                type="text"
                placeholder="Description"
                className="search-input w-full"
                value={categoryForm.description}
                onChange={e => setCategoryForm({...categoryForm, description: e.target.value})}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '13px', color: '#4b5563' }}>Category Theme Color</span>
                <input
                  type="color"
                  style={{ width: '100%', height: '38px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer' }}
                  value={categoryForm.color}
                  onChange={e => setCategoryForm({...categoryForm, color: e.target.value})}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Create Category</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. Issue Book Modal */}
      {showIssueModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '460px', overflow: 'visible' }}>
            <h2 style={{ marginBottom: '4px' }}>Issue Book Copy</h2>

            {/* Show pre-selected book context */}
            {selectedBook && (
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px' }}>
                <div style={{ fontWeight: '600', color: '#1e40af' }}>📚 {selectedBook.title}</div>
                <div style={{ color: '#3b82f6', marginTop: '2px' }}>Acc No: {selectedBook.accession_number} &nbsp;|&nbsp; {selectedBook.available_copies} of {selectedBook.total_copies} copies available</div>
              </div>
            )}

            <form onSubmit={handleIssueSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Member Search */}
              <div style={{ position: 'relative' }}>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Borrower Member (Search Name or ID)</span>
                <input
                  type="text"
                  placeholder="Type to search borrower name or ID..."
                  required
                  className="search-input w-full"
                  value={memberSearchQuery}
                  onChange={handleMemberSearchChange}
                  onFocus={() => { if (matchedMembers.length > 0) setShowMemberDropdown(true); }}
                  onBlur={() => { setTimeout(() => setShowMemberDropdown(false), 200); }}
                />
                {showMemberDropdown && matchedMembers.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: 'white', border: '1px solid #d1d5db', borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 1050,
                    maxHeight: '180px', overflowY: 'auto', marginTop: '4px'
                  }}>
                    {matchedMembers.map(m => (
                      <div key={m.id} onClick={() => handleSelectMember(m)}
                        style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' }}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{m.user_name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280', display: 'flex', gap: '8px' }}>
                          <span>Mem ID: {m.membership_id}</span><span>•</span><span style={{ textTransform: 'capitalize' }}>{m.user_role}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <input type="hidden" value={selectedMember ? selectedMember.id : issueForm.member_id} required />
              </div>

              {/* Book Copy Selection */}
              <div>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>
                  Book Copy Barcode / Accession No.
                </span>
                {/* Dropdown of available copy barcodes if book was pre-selected */}
                {selectedBook && selectedBook.copies && selectedBook.copies.filter(c => c.status === 'AVAILABLE').length > 0 ? (
                  <select
                    className="filter-select w-full"
                    required
                    value={issueForm.copy_barcode}
                    onChange={e => setIssueForm({...issueForm, copy_barcode: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px' }}
                  >
                    <option value="">-- Select an Available Copy --</option>
                    {selectedBook.copies.filter(c => c.status === 'AVAILABLE').map(c => (
                      <option key={c.id} value={c.barcode}>{c.barcode} ({c.condition})</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    placeholder="Enter copy barcode (e.g. LIBxxxxxxxx) or book accession no. (e.g. ACC1116E427)"
                    required
                    className="search-input w-full"
                    value={issueForm.copy_barcode}
                    onChange={e => setIssueForm({...issueForm, copy_barcode: e.target.value})}
                  />
                )}
                <span style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginTop: '4px' }}>
                  💡 You can enter the copy barcode (printed on book) or the book's Acc No. (e.g. ACC1116E427) — the system will auto-select the first available copy.
                </span>
              </div>

              {/* Issue Duration */}
              <div>
                <span style={{ fontSize: '13px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Issue Duration (Days)</span>
                <input type="number" min="1" max="365" required className="search-input w-full"
                  value={issueForm.issue_days}
                  onChange={e => setIssueForm({...issueForm, issue_days: parseInt(e.target.value) || 14})} />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="button" onClick={() => { setShowIssueModal(false); setSelectedBook(null); }} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Issue Book</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. Return Book Modal */}
      {showReturnModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h2>Return Book</h2>
            <p>Book: <strong>{returnForm.bookTitle}</strong></p>
            <p>Borrower: <strong>{returnForm.memberName}</strong></p>
            {returnForm.fine_amount > 0 && (
              <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '6px', color: '#b91c1c', marginBottom: '12px' }}>
                Estimated Overdue Fine: <strong>₹{returnForm.fine_amount}</strong>
              </div>
            )}
            <form onSubmit={handleReturnSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <select className="filter-select w-full" value={returnForm.condition} onChange={e => setReturnForm({...returnForm, condition: e.target.value})}>
                <option value="GOOD">Good Condition</option>
                <option value="DAMAGED">Damaged Condition</option>
                <option value="LOST">Lost Book Copy</option>
              </select>
              <textarea placeholder="Condition notes / Damage description..." className="search-input w-full" value={returnForm.damage_notes} onChange={e => setReturnForm({...returnForm, damage_notes: e.target.value})} />
              
              {returnForm.fine_amount > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={returnForm.waive_fine} onChange={e => setReturnForm({...returnForm, waive_fine: e.target.checked})} />
                  Waive Overdue Fines
                </label>
              )}
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowReturnModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Process Return</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Fine Payment Modal */}
      {showFineModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h2>Process Fine Payment</h2>
            <p>Total Outstanding Amount: <strong>₹{fineForm.totalAmount}</strong></p>
            <form onSubmit={handleFineSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input type="number" placeholder="Amount Paying" required className="search-input w-full" value={fineForm.amount_paid} onChange={e => setFineForm({...fineForm, amount_paid: parseFloat(e.target.value) || 0})} />
              <select className="filter-select w-full" value={fineForm.payment_method} onChange={e => setFineForm({...fineForm, payment_method: e.target.value})}>
                <option value="CASH">Cash</option>
                <option value="CARD">Card / POS</option>
                <option value="ONLINE">Online Portal</option>
              </select>
              <input type="text" placeholder="Transaction Reference No." className="search-input w-full" value={fineForm.transaction_reference} onChange={e => setFineForm({...fineForm, transaction_reference: e.target.value})} />
              <textarea placeholder="Payment Notes" className="search-input w-full" value={fineForm.notes} onChange={e => setFineForm({...fineForm, notes: e.target.value})} />
              
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowFineModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 5. Upload Digital Resource Modal */}
      {showDigitalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '450px' }}>
            <h2>Upload Digital Library Resource</h2>
            <form onSubmit={handleDigitalSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input type="text" placeholder="Resource Title" required className="search-input w-full" value={digitalForm.title} onChange={e => setDigitalForm({...digitalForm, title: e.target.value})} />
              <input type="text" placeholder="Authors / Creators" required className="search-input w-full" value={digitalForm.authors} onChange={e => setDigitalForm({...digitalForm, authors: e.target.value})} />
              
              <select className="filter-select w-full" value={digitalForm.resource_type} onChange={e => setDigitalForm({...digitalForm, resource_type: e.target.value})}>
                <option value="EBOOK">E-Book</option>
                <option value="NOTES">Lecture Notes</option>
                <option value="PAPER">Research Paper</option>
                <option value="QBANK">Question Bank</option>
                <option value="REFERENCE">Reference Book</option>
                <option value="OTHER">Other</option>
              </select>
              
              <select className="filter-select w-full" value={digitalForm.access_level} onChange={e => setDigitalForm({...digitalForm, access_level: e.target.value})}>
                <option value="ALL">Public Access (All Users)</option>
                <option value="STUDENT">Student Only Access</option>
                <option value="FACULTY">Faculty Only Access</option>
              </select>

              {digitalForm.access_level === 'STUDENT' && (
                <div style={{ padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600', color: '#4b5563' }}>Restrict to Courses (Optional):</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { key: 'MBBS', label: 'MBBS' },
                      { key: 'PG', label: 'Post Graduate' },
                      { key: 'SUPER_SPECIALITY', label: 'Super Speciality' },
                      { key: 'INTERNSHIP', label: 'Internship' }
                    ].map(course => {
                      const isChecked = (digitalForm.allowed_courses || []).includes(course.key);
                      return (
                        <label key={course.key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: '#1f2937' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              const list = [...(digitalForm.allowed_courses || [])];
                              if (e.target.checked) {
                                list.push(course.key);
                              } else {
                                const index = list.indexOf(course.key);
                                if (index > -1) list.splice(index, 1);
                              }
                              setDigitalForm({ ...digitalForm, allowed_courses: list });
                            }}
                          />
                          {course.label}
                        </label>
                      );
                    })}
                  </div>
                  <span style={{ fontSize: '11px', color: '#9ca3af' }}>If none selected, all students can access.</span>
                </div>
              )}

              <input type="text" placeholder="External URL Link" className="search-input w-full" value={digitalForm.external_url} onChange={e => setDigitalForm({...digitalForm, external_url: e.target.value})} />
              <input type="file" onChange={e => setDigitalForm({...digitalForm, file: e.target.files[0]})} />
              <textarea placeholder="Description" className="search-input w-full" value={digitalForm.description} onChange={e => setDigitalForm({...digitalForm, description: e.target.value})} />
              <input type="text" placeholder="Tags (comma separated)" className="search-input w-full" value={digitalForm.tags} onChange={e => setDigitalForm({...digitalForm, tags: e.target.value})} />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowDigitalModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Upload</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 6. Add/Edit Journal Modal */}
      {showJournalModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '450px' }}>
            <h2>{currentJournal ? 'Edit Subscription' : 'New Journal Subscription'}</h2>
            <form onSubmit={handleJournalSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input type="text" placeholder="Journal Name" required className="search-input w-full" value={journalForm.name} onChange={e => setJournalForm({...journalForm, name: e.target.value})} />
              <input type="text" placeholder="ISSN (e.g. 0028-0836)" required className="search-input w-full" value={journalForm.issn} onChange={e => setJournalForm({...journalForm, issn: e.target.value})} />
              <input type="text" placeholder="Publisher" required className="search-input w-full" value={journalForm.publisher} onChange={e => setJournalForm({...journalForm, publisher: e.target.value})} />
              <input type="text" placeholder="Publication Frequency (e.g. Monthly)" className="search-input w-full" value={journalForm.frequency} onChange={e => setJournalForm({...journalForm, frequency: e.target.value})} />
              
              <select className="filter-select w-full" value={journalForm.journal_type} onChange={e => setJournalForm({...journalForm, journal_type: e.target.value})}>
                <option value="PRINT">Print Subscription</option>
                <option value="DIGITAL">Digital Access Portal</option>
                <option value="BOTH">Print & Digital Bundle</option>
              </select>

              <input type="date" placeholder="Subscription Start" required className="search-input w-full" value={journalForm.subscription_start} onChange={e => setJournalForm({...journalForm, subscription_start: e.target.value})} />
              <input type="date" placeholder="Subscription End" required className="search-input w-full" value={journalForm.subscription_end} onChange={e => setJournalForm({...journalForm, subscription_end: e.target.value})} />
              <input type="number" placeholder="Cost" className="search-input w-full" value={journalForm.subscription_cost} onChange={e => setJournalForm({...journalForm, subscription_cost: parseFloat(e.target.value) || 0})} />
              <input type="text" placeholder="Digital Portal Access URL" className="search-input w-full" value={journalForm.digital_url} onChange={e => setJournalForm({...journalForm, digital_url: e.target.value})} />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowJournalModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Save Subscription</button>
              </div>
            </form>
          </div>
        </div>
      )}



      {/* 8. Settings Form Modal */}
      {showSettingsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '12px', width: '450px' }}>
            <h2>Edit Regulations Rules & Policy Rates</h2>
            <form onSubmit={handleSettingsSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <label>Student Overdue Fine Rate (₹/day)</label>
              <input type="number" required className="search-input w-full" value={settingsForm.fine_rate_student} onChange={e => setSettingsForm({...settingsForm, fine_rate_student: parseInt(e.target.value) || 0})} />
              
              <label>Faculty Overdue Fine Rate (₹/day)</label>
              <input type="number" required className="search-input w-full" value={settingsForm.fine_rate_faculty} onChange={e => setSettingsForm({...settingsForm, fine_rate_faculty: parseInt(e.target.value) || 0})} />
              
              <label>Student Issue Duration (Days)</label>
              <input type="number" required className="search-input w-full" value={settingsForm.duration_student} onChange={e => setSettingsForm({...settingsForm, duration_student: parseInt(e.target.value) || 0})} />

              <label>Faculty Issue Duration (Days)</label>
              <input type="number" required className="search-input w-full" value={settingsForm.duration_faculty} onChange={e => setSettingsForm({...settingsForm, duration_faculty: parseInt(e.target.value) || 0})} />

              <label>Max Books Student Can Borrow</label>
              <input type="number" required className="search-input w-full" value={settingsForm.max_books_student} onChange={e => setSettingsForm({...settingsForm, max_books_student: parseInt(e.target.value) || 0})} />

              <label>Max Books Faculty Can Borrow</label>
              <input type="number" required className="search-input w-full" value={settingsForm.max_books_faculty} onChange={e => setSettingsForm({...settingsForm, max_books_faculty: parseInt(e.target.value) || 0})} />

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '12px' }}>
                <button type="button" onClick={() => setShowSettingsModal(false)} className="btn-sm btn-outline">Cancel</button>
                <button type="submit" className="btn-sm btn-primary">Save Policies</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default LibraryDashboard;